import assetUtils, { Asset } from 'app-lib/assetUtils'
import IBorrowedFundsState from 'bzx-common/src/domain/IBorrowedFundsState'
import ManageCollateralRequest from 'bzx-common/src/domain/ManageCollateralRequest'
import {
  PositionType,
  TradeType,
  IOpenPositionProps,
  IClosePositionRequest,
} from 'bzx-common/src/domain/trade.types'
import * as mobx from 'mobx'
import { BigNumber } from '@0x/utils'
import RootStore from '../RootStore'
import TradeStore from './TradeStore'
import loanUtils from 'bzx-common/src/lib/loanUtils'
import tradeUtils, { getPairConfig, IPairConfigDetails } from 'bzx-common/src/lib/tradeUtils'
import { ChainId } from 'bzx-common/src/config/chains/chainCommon'

export default class OpenPosition implements IOpenPositionProps {
  activeTokenLiquidation = Asset.IETH
  activeTokenProfit = Asset.IETH
  baseToken = Asset.IETH
  chainId: ChainId
  collateral = new BigNumber(0)
  /** oracle price of collateral / principal  */
  currentPrice = new BigNumber(0)
  isLoading = true
  leverage = 0
  liquidationPrice = new BigNumber(0)
  loan: IBorrowedFundsState
  loanApr = new BigNumber(NaN)
  maintenanceMargin = new BigNumber(0)
  openPrice = new BigNumber(0)
  pairConfig: IPairConfigDetails
  pendingTx = false
  positionType = PositionType.LONG
  positionValue = new BigNumber(0)
  profitCollateralToken = new BigNumber(0)
  profitLoanToken = new BigNumber(0)
  profitUSD = new BigNumber(0)
  quoteToken = Asset.IETH
  rootStore: RootStore
  tradeStore: TradeStore
  value = new BigNumber(0)
  valueChange = new BigNumber(0)

  get isLong() {
    return this.positionType === PositionType.LONG
  }

  get liquidationPriceSwitch() {
    return this.activeTokenLiquidation === this.loan.collateralAsset
      ? this.liquidationPrice
      : new BigNumber(1).div(this.liquidationPrice)
  }

  get collateralizedPercent() {
    return this.loan.collateralizedPercent.multipliedBy(100)
  }

  /** get oracle price of collateral / principal */
  async getCurrentPrice() {
    const fp = this.rootStore.fulcrumProvider
    const currentPrice = await tradeUtils.getLoanMarketRate(fp, this.loan, this.isLong)
    this.assign({ currentPrice })
    return currentPrice
  }

  setActiveTokenProfit = (asset: Asset) => {
    this.set('activeTokenProfit', asset)
  }

  setActiveTokenLiquidation = (asset: Asset) => {
    this.set('activeTokenLiquidation', asset)
  }

  onManageClick = () => {
    const loan = this.loan
    if (!loan) {
      return
    }
    const { loanId, collateralAsset, loanAsset } = loan
    const manageCollateralRequest = new ManageCollateralRequest(
      loanId,
      collateralAsset,
      this.collateral,
      false,
      loanAsset,
      this.positionType
    )
    this.tradeStore.requestManageCollateral(manageCollateralRequest)
  }

  async closePosition() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return
    }
    const tradeRequest: IClosePositionRequest = {
      dex: this.pairConfig.dexes[0],
      tradeType: TradeType.CLOSE_POSITION,
      baseToken: this.baseToken,
      quoteToken: this.quoteToken,
      depositToken: this.baseToken, // avoid an issue until close and open requests are split
      positionType: this.positionType,
      leverage: this.leverage,
      amount: new BigNumber(0),
      loanId: this.loan.loanId,
      returnTokenIsCollateral: false,
      loanDataBytes: '0x',
    }
    this.tradeStore.requestClosePosition(tradeRequest, this)
  }

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof IOpenPositionProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: { [key: string]: any }) {
    Object.assign(this, props)
  }

  /** Get how much interest (%) the user is paying currently */
  async getLoanApr() {
    const fp = this.rootStore.fulcrumProvider
    const loanApr = await loanUtils.getBorrowInterestRate(fp, this.loan.loanAsset)
    this.assign({ loanApr })
    return loanApr
  }

  syncData() {
    let openValue = new BigNumber(0)
    let valueChange = new BigNumber(0)
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return
    }
    if (this.positionType === PositionType.LONG) {
      const collateralAssetDecimals = assetUtils.getDecimals(chainId, this.loan.collateralAsset)
      openValue = this.loan.loanData.collateral
        .div(10 ** collateralAssetDecimals)
        .times(this.openPrice)
      valueChange = this.value.minus(openValue).div(openValue).times(100)
    } else {
      const loanAssetDecimals = assetUtils.getDecimals(chainId, this.loan.loanAsset)
      openValue = this.loan.loanData.principal.div(10 ** loanAssetDecimals).times(this.openPrice)
      valueChange = this.value.minus(openValue).div(openValue).times(100)
    }

    this.assign({ valueChange, isLoading: false })
  }

  init() {
    const { chainId } = this.rootStore.web3Connection

    chainId &&
      this.set(
        'activeTokenLiquidation',
        assetUtils.getDefaultActiveToken(chainId, this.baseToken, this.quoteToken)
      )

    this.setActiveTokenProfit(this.baseToken)
    this.syncData()
  }

  constructor(rootStore: RootStore, position: IOpenPositionProps) {
    const { baseToken, quoteToken, chainId } = position
    this.rootStore = rootStore
    this.tradeStore = rootStore.tradeStore
    this.chainId = position.chainId // Make TS happy
    this.pairConfig = getPairConfig({ baseToken, quoteToken }, chainId)
    this.assign(position)
    this.loan = position.loan
    mobx.makeAutoObservable(this, undefined, { deep: false, autoBind: true })
    this.init()
  }
}
